import { useMutation, useQuery } from '@tanstack/react-query';
import { ISystemMail } from '../../Interfaces/ISystemMail';
import { customFetcher } from '../useFetch';
import { ISystemMailCategory } from '../../Interfaces/ISystemMailCategory';
import { ISystemMail_has_Dictionary } from '../../Interfaces/ISystemMail_has_Dictionary';
import { IDictionaryEntry } from '../../Interfaces/IDictionaryEntry';
import { IDictionary } from '../../Interfaces/IDictionary';

export const useGetSystemMails = () => {
  const {
    data: systemMails,
    isLoading: isLoadingSystemMails,
    refetch: refetchSystemMails,
  } = useQuery<ISystemMail[]>({
    queryKey: ['systemmail'],
    queryFn: () => customFetcher({ endpoint: '/systemmail' }),
  });

  return { systemMails, isLoadingSystemMails, refetchSystemMails };
};

export const useGetSystemMail = (systemMailId: number) => {
  const {
    data: systemMail,
    isLoading: isLoadingSystemMail,
    refetch,
  } = useQuery<ISystemMail>({
    refetchOnWindowFocus: false,
    queryKey: ['systemmail', systemMailId],
    queryFn: () => customFetcher({ endpoint: `/systemmail/${systemMailId}` }),
  });

  return { systemMail, isLoadingSystemMail, refetch };
};

export const useUpdateSystemMail = () => {
  const { mutateAsync: updateSystemMail, isPending: isPendingSystemMail } =
    useMutation({
      mutationFn: (uploadObject: ISystemMail) =>
        customFetcher({
          endpoint: '/systemmail',
          body: uploadObject,
          method: 'PUT',
        }),
    });
  return {
    updateSystemMail,
    isPendingSystemMail,
  };
};

export const useGetSystemMailCategories = () => {
  const {
    data: systemMailCategories,
    isLoading: isLoadingSystemMailCategories,
  } = useQuery<ISystemMailCategory[]>({
    queryKey: ['systemmailcategory'],
    queryFn: () => customFetcher({ endpoint: '/systemmailcategory' }),
  });

  return { systemMailCategories, isLoadingSystemMailCategories };
};

export const useGetSystemMailHasDictionary = () => {
  const {
    data: systemMailHasDictionary,
    isLoading: isLoadingSystemMailHasDictionary,
    refetch: refetchSystemMailHasDictionary,
  } = useQuery<ISystemMail_has_Dictionary[]>({
    queryKey: ['systemmail_has_dictionary'],
    queryFn: () => customFetcher({ endpoint: '/systemmail_has_dictionary' }),
  });

  return {
    systemMailHasDictionary,
    isLoadingSystemMailHasDictionary,
    refetchSystemMailHasDictionary,
  };
};

export const useCreateSystemMailHasDictionary = () => {
  const {
    mutateAsync: createSystemMailHasDictionary,
    isPending: isPendingCreateSystemMailHasDictionary,
  } = useMutation({
    mutationFn: (uploadObject: ISystemMail_has_Dictionary) =>
      customFetcher({
        endpoint: '/systemmail_has_dictionary',
        body: uploadObject,
        method: 'POST',
      }),
  });
  return {
    createSystemMailHasDictionary,
    isPendingCreateSystemMailHasDictionary,
  };
};

export const useDeleteSystemMailHasDictionary = () => {
  const {
    mutateAsync: deleteSystemMailHasDictionary,
    isPending: isPendingDeleteSystemMailHasDictionary,
  } = useMutation({
    mutationFn: (uploadObject: ISystemMail_has_Dictionary) =>
      customFetcher({
        endpoint: `/systemmail_has_dictionary/${uploadObject.idSystemMail}/${uploadObject.idDictionary}`,
        method: 'DELETE',
      }),
  });
  return {
    deleteSystemMailHasDictionary,
    isPendingDeleteSystemMailHasDictionary,
  };
};
